import React, { useState, useEffect, useRef } from 'react';
import { Sidebar } from './Sidebar';
import { cn } from '@/lib/utils';
import { Bell, Search, Settings, ExternalLink, FolderOpen, X, Check, MessageCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import SupportChatWidget from '@/components/shared/SupportChatWidget';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Admin emails that should see the Google Drive link
const ADMIN_EMAILS_WITH_DRIVE_ACCESS = [
  'nadia@elevatedentalacademy.co.uk',
  'nreinolds@gmail.com',
  'fatima@elevatedentalacademy.co.uk',
  'gaurav@elevatedentalacademy.co.uk',
  'gaurav@gmdentist.co.uk',
  'gmd.fatimarey@gmail.com',
  'admin@elevate360.com',
  'super_admin@elevate360.com'
];

const ALL_COURSE_DETAILS_DRIVE_LINK = 'https://drive.google.com/drive/folders/1tQdUMkz3InpsqtDx_RHp1XbMgXPTLLwy';

export const DashboardLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);
  const { user } = useAuth();
  
  // Check if current user should see the Google Drive link
  const showDriveLink = user && ADMIN_EMAILS_WITH_DRIVE_ACCESS.includes(user.email?.toLowerCase());

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications]);

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const token = localStorage.getItem('elevate360_token');
      const response = await axios.get(`${API_URL}/api/support/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('elevate360_token');
      await axios.put(`${API_URL}/api/support/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const unreadCount = notifications.length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      
      {/* Main Content */}
      <div
        className={cn(
          'min-h-screen',
          collapsed ? 'md:ml-20' : 'md:ml-64',
          'ml-0'
        )}
        style={{ transition: 'margin-left 0.2s ease-in-out' }}
      >
        {/* Google Drive Banner - Only for specific admins */}
        {showDriveLink && (
          <div className="bg-gradient-to-r from-[#0D1B2A] to-[#1B3A4B] text-white px-6 py-2">
            <a 
              href={ALL_COURSE_DETAILS_DRIVE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              <FolderOpen className="w-4 h-4 text-[#41EAD4]" />
              <span className="text-sm font-medium">All Course Details</span>
              <ExternalLink className="w-3 h-3 text-[#41EAD4]" />
            </a>
          </div>
        )}

        {/* Header - White Banner with Logo */}
        <header className="sticky top-0 z-20 bg-white shadow-sm border-b border-slate-200 px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Logo for mobile - visible only on small screens */}
            <div className="md:hidden flex items-center">
              <img 
                src="/logo-plan4growth.png"
                alt="Plan 4 Growth"
                className="h-10 w-auto"
              />
            </div>
            
            {/* Search */}
            <div className="flex-1 max-w-md hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search courses, assignments..."
                  className="pl-10 bg-slate-50 border-slate-200 focus:ring-[#41EAD4] focus:border-[#41EAD4]"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 ml-auto">
              <div className="relative" ref={notificationRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-slate-600 hover:text-[#0D1B2A]"
                  data-testid="notifications-btn"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
                    <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-semibold text-[#0D1B2A]">Notifications</h3>
                      <button 
                        onClick={() => setShowNotifications(false)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="max-h-80 overflow-y-auto">
                      {loadingNotifications ? (
                        <div className="p-4 text-center text-slate-500">
                          Loading...
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-6 text-center">
                          <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500 text-sm">No new notifications</p>
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div 
                            key={notif.notification_id}
                            className="p-3 border-b border-slate-50 hover:bg-slate-50 flex items-start gap-3"
                          >
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <MessageCircle className="w-4 h-4 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#0D1B2A] truncate">{notif.title}</p>
                              <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            <button
                              onClick={() => markAsRead(notif.notification_id)}
                              className="text-slate-400 hover:text-green-600 p-1"
                              title="Mark as read"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {notifications.length > 0 && (
                      <div className="p-2 border-t border-slate-100">
                        <button 
                          onClick={() => {
                            notifications.forEach(n => markAsRead(n.notification_id));
                          }}
                          className="w-full text-center text-sm text-[#41EAD4] hover:text-[#0D1B2A] py-1"
                        >
                          Mark all as read
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-600 hover:text-[#0D1B2A]"
                data-testid="settings-btn"
              >
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-8">
          {children}
        </main>
      </div>

      {/* Support Chat Widget - For all logged-in users */}
      <SupportChatWidget user={user} />
    </div>
  );
};

export default DashboardLayout;
